/**
 * 资产库 API
 * 
 * 提供资产管理相关的接口调用
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-10-23
 */

import { http } from '@/utils/http'
import { unwrap, silentApiClient } from '@/utils/apiClient'
import type {
  Asset,
  AssetFilter,
  AssetSort,
  AssetListResponse,
  AssetStats,
  AssetBatchRequest,
  AssetBatchResponse,
  AssetMergeRequest,
  AssetExportRequest,
  AssetTag,
  AssetSearchSuggestion,
  SignedURLRequest,
  SignedURLInfo,
  AssetGroup,
  AssetGroupFilter,
  AssetGroupSort,
  AssetGroupListResponse,
  CreateGroupRequest,
  UpdateGroupRequest,
  BatchMoveToGroupRequest,
  LibraryQuota,
  AssetExpiryInfo,
  PromoteToLibraryResponse
} from '@/types/library.types'

/**
 * 资产库 API 类
 */
export class LibraryAPI {
  private baseURL = '/api/v1/library'

  /**
   * 获取资产列表
   * 
   * @param page 页码
   * @param pageSize 每页数量
   * @param filter 过滤条件
   * @param sort 排序条件
   * @returns 资产列表响应
   */
  async getAssets(
    page: number = 1,
    pageSize: number = 20,
    filter?: AssetFilter,
    sort?: AssetSort
  ): Promise<AssetListResponse> {
    const params: any = {
      page,
      pageSize,
      ...filter
    }

    if (sort) {
      params.sortField = sort.field
      params.sortOrder = sort.order
    }

    return unwrap<AssetListResponse>(() =>
      http.get(this.baseURL, { params })
    )
  }

  /**
   * 获取单个资产详情
   * 
   * @param assetId 资产ID
   * @returns 资产详情
   */
  async getAsset(assetId: string): Promise<Asset> {
    return unwrap<Asset>(() =>
      http.get(`${this.baseURL}/${assetId}`)
    )
  }

  /**
   * 更新资产信息
   * 
   * @param assetId 资产ID
   * @param updates 更新内容
   * @returns 更新后的资产
   */
  async updateAsset(
    assetId: string,
    updates: Partial<Pick<Asset, 'filename' | 'tags' | 'favorite' | 'meta'>>
  ): Promise<Asset> {
    return unwrap<Asset>(() =>
      http.patch(`${this.baseURL}/${assetId}`, updates)
    )
  }

  /**
   * 删除资产
   * 
   * @param assetId 资产ID
   */
  async deleteAsset(assetId: string): Promise<void> {
    return unwrap<void>(() =>
      http.delete(`${this.baseURL}/${assetId}`)
    )
  }

  /**
   * 批量删除资产
   *
   * @param assetIds 资产ID列表
   * @returns 批量操作响应
   */
  async deleteAssets(assetIds: string[]): Promise<AssetBatchResponse> {
    return unwrap<AssetBatchResponse>(() =>
      http.post(`${this.baseURL}/batch`, {
        assetIds,
        operation: 'delete'
      })
    )
  }

  /**
   * 切换收藏状态
   * 
   * @param assetId 资产ID
   * @param favorite 是否收藏
   * @returns 更新后的资产
   */
  async toggleFavorite(assetId: string, favorite: boolean): Promise<Asset> {
    return unwrap<Asset>(() =>
      http.patch(`${this.baseURL}/${assetId}/favorite`, { favorite })
    )
  }

  /**
   * 批量操作
   * 
   * @param request 批量操作请求
   * @returns 批量操作响应
   */
  async batchOperation(request: AssetBatchRequest): Promise<AssetBatchResponse> {
    return unwrap<AssetBatchResponse>(() =>
      http.post(`${this.baseURL}/batch`, request)
    )
  }

  /**
   * 合并资产
   *
   * @param request 合并请求
   * @returns 合并任务信息
   */
  async mergeAssets(request: AssetMergeRequest): Promise<{ taskId: string }> {
    return unwrap<{ taskId: string }>(() =>
      http.post(`${this.baseURL}/merge`, request)
    )
  }

  /**
   * 导出资产
   * 
   * @param request 导出请求
   * @returns 导出文件URL
   */
  async exportAssets(request: AssetExportRequest): Promise<{ url: string; filename: string }> {
    return unwrap<{ url: string; filename: string }>(() =>
      http.post(`${this.baseURL}/export`, request)
    )
  }

  /**
   * 获取资产统计
   * 
   * @returns 资产统计信息
   */
  async getStats(): Promise<AssetStats> {
    return unwrap<AssetStats>(() =>
      http.get(`${this.baseURL}/stats`)
    )
  }

  /**
   * 获取所有标签
   * 
   * @returns 标签列表
   */
  async getTags(): Promise<AssetTag[]> {
    return unwrap<AssetTag[]>(() =>
      http.get(`${this.baseURL}/tags`)
    )
  }

  /**
   * 搜索建议
   * 
   * @param keyword 关键词
   * @returns 搜索建议列表
   */
  async getSearchSuggestions(keyword: string): Promise<AssetSearchSuggestion[]> {
    return unwrap<AssetSearchSuggestion[]>(() =>
      http.get(`${this.baseURL}/search/suggestions`, {
        params: { keyword }
      })
    )
  }

  /**
   * 下载资产
   * 
   * @param assetId 资产ID
   * @returns 下载URL
   */
  async downloadAsset(assetId: string): Promise<{ url: string }> {
    return unwrap<{ url: string }>(() =>
      http.get(`${this.baseURL}/${assetId}/download`)
    )
  }

  /**
   * 批量下载资产
   *
   * @param assetIds 资产ID列表
   * @returns 打包下载URL
   */
  async downloadAssets(assetIds: string[]): Promise<{ url: string; filename: string }> {
    return unwrap<{ url: string; filename: string }>(() =>
      http.post(`${this.baseURL}/batch/download`, { assetIds })
    )
  }

  /**
   * 获取签名URL
   *
   * @param assetId 资产ID
   * @param request 签名请求
   * @returns 签名URL信息
   */
  async getSignedURL(assetId: string, request: SignedURLRequest): Promise<SignedURLInfo> {
    // 使用 silentApiClient 避免显示错误消息，由调用方统一处理
    return silentApiClient.call<SignedURLInfo>(() =>
      http.post(`${this.baseURL}/${assetId}/signed-url`, request, {
        // 添加自定义标记，告诉 HTTP 拦截器不要显示错误消息
        headers: {
          'X-Silent-Error': 'true'
        }
      } as any)
    )
  }

  // ============= 分组管理相关方法 =============

  /**
   * 获取分组列表
   *
   * @param page 页码
   * @param pageSize 每页数量
   * @param filter 过滤条件
   * @param sort 排序条件
   * @returns 分组列表响应
   */
  async getGroups(
    page: number = 1,
    pageSize: number = 20,
    filter?: AssetGroupFilter,
    sort?: AssetGroupSort
  ): Promise<AssetGroupListResponse> {
    const params: any = {
      page,
      pageSize,
      ...filter
    }

    if (sort) {
      params.sortField = sort.field
      params.sortOrder = sort.order
    }

    return unwrap<AssetGroupListResponse>(() =>
      http.get(`${this.baseURL}/groups`, { params })
    )
  }

  /**
   * 获取单个分组详情
   *
   * @param groupId 分组ID
   * @returns 分组详情
   */
  async getGroup(groupId: string): Promise<AssetGroup> {
    return unwrap<AssetGroup>(() =>
      http.get(`${this.baseURL}/groups/${groupId}`)
    )
  }

  /**
   * 创建分组
   *
   * @param request 创建分组请求
   * @returns 创建的分组
   */
  async createGroup(request: CreateGroupRequest): Promise<AssetGroup> {
    return unwrap<AssetGroup>(() =>
      http.post(`${this.baseURL}/groups`, request)
    )
  }

  /**
   * 更新分组
   *
   * @param groupId 分组ID
   * @param request 更新分组请求
   */
  async updateGroup(groupId: string, request: UpdateGroupRequest): Promise<void> {
    return unwrap<void>(() =>
      http.patch(`${this.baseURL}/groups/${groupId}`, request)
    )
  }

  /**
   * 删除分组
   *
   * @param groupId 分组ID
   */
  async deleteGroup(groupId: string): Promise<void> {
    return unwrap<void>(() =>
      http.delete(`${this.baseURL}/groups/${groupId}`)
    )
  }

  /**
   * 批量移动资产到分组
   *
   * @param request 批量移动请求
   */
  async batchMoveToGroup(request: BatchMoveToGroupRequest): Promise<void> {
    return unwrap<void>(() =>
      http.post(`${this.baseURL}/groups/batch-move`, request)
    )
  }

  // ============= 作品库管理相关方法 =============

  /**
   * 获取作品库配额信息
   *
   * @returns 作品库配额信息
   */
  async getLibraryQuota(): Promise<LibraryQuota> {
    return unwrap<LibraryQuota>(() =>
      http.get(`${this.baseURL}/quota`)
    )
  }

  /**
   * 获取资产过期信息
   *
   * @param assetId 资产ID
   * @returns 资产过期信息
   */
  async getAssetExpiryInfo(assetId: string): Promise<AssetExpiryInfo> {
    return unwrap<AssetExpiryInfo>(() =>
      http.get(`${this.baseURL}/assets/${assetId}/expiry`)
    )
  }

  /**
   * 提升资产到作品库
   *
   * @param assetId 资产ID
   * @returns 提升响应
   */
  async promoteToLibrary(assetId: string): Promise<PromoteToLibraryResponse> {
    return unwrap<PromoteToLibraryResponse>(() =>
      http.post(`${this.baseURL}/assets/${assetId}/promote`)
    )
  }
}

// 导出单例
export const libraryAPI = new LibraryAPI()

